// Cycle-16 mention component set. Mirrors the iOS-A / AND-A
// `MentionPill` variants that come from the Figma `Mention` component
// set (`2918:10419`). The pill is purely presentational — host code
// supplies `displayName` and the `onPress` handler that opens the
// profile or filters to the mentioned user.
//
// Figma renders mentions as *tinted* inline chips (not solid pills):
// `tagged-other` carries a neutral white/black tint, while `tagged-own`,
// `tagged-all`, and `tag-you` all use an indigo @15% tint with indigo
// foreground. `tagged-all` reads "All" with a leading group glyph.

import React, { useMemo } from 'react';
import { Image, Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { Users } from 'lucide-react-native';
import { tokens } from '../design-system/tokens';
import type { MentionPillVariant } from './MentionPillVariant';

export { MENTION_PILL_VARIANTS, type MentionPillVariant } from './MentionPillVariant';

export interface MentionPillProps {
  variant: MentionPillVariant;
  displayName: string;
  onPress?: (() => void) | undefined;
  testID?: string | undefined;
  // Optional leading 16x16 (radius 4) avatar for `tagged-other` /
  // `tagged-own`. `avatarUri` wins; otherwise `avatarInitials` renders a
  // tinted placeholder. Ignored for `tagged-all` (group glyph) and
  // `tag-you` (no avatar in Figma).
  avatarUri?: string | undefined;
  avatarInitials?: string | undefined;
}

// Brand indigo @15% (`#537EFF26`) — a tinted highlight rather than a solid
// fill, so mentions sit inside a paragraph without pulling focus. `#537EFF`
// is the brand indigo; `26` is the 15% alpha suffix.
//
// Per-variant className map. The pill is a tinted chip: `tagged-own`,
// `tagged-all`, and `tag-you` use brand-indigo text on a brand-indigo @15%
// tint. `tagged-other` keeps a readable foreground (light = primary text,
// dark = #F5F6F8) on the same brand-indigo @15% tint.
interface VariantClasses {
  background: string;
  text: string;
}

const VARIANT_CLASSES: Readonly<Record<MentionPillVariant, VariantClasses>> = {
  'tag-you': {
    background: 'bg-[#537EFF26]',
    text: 'text-[#537EFF] dark:text-[#537EFF]',
  },
  'tagged-other': {
    background: 'bg-[#537EFF26]',
    text: 'text-[#1C1C1E] dark:text-[#F5F6F8]',
  },
  'tagged-own': {
    background: 'bg-[#537EFF26]',
    text: 'text-[#537EFF] dark:text-[#537EFF]',
  },
  'tagged-all': {
    background: 'bg-[#537EFF26]',
    text: 'text-[#537EFF] dark:text-[#537EFF]',
  },
};

// Foreground hex used for the leading glyph / avatar-fallback initials,
// which are colored via the `color` prop (not className) since they render
// inside native `<Image>`/icon components.
const FOREGROUND_HEX: Readonly<Record<MentionPillVariant, { light: string; dark: string }>> = {
  'tag-you': { light: '#537EFF', dark: '#537EFF' },
  'tagged-other': { light: '#1C1C1E', dark: '#F5F6F8' },
  'tagged-own': { light: '#537EFF', dark: '#537EFF' },
  'tagged-all': { light: '#537EFF', dark: '#537EFF' },
};

export const MentionPill = React.memo<MentionPillProps>(
  ({ variant, displayName, onPress, testID, avatarUri, avatarInitials }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const variantClasses = VARIANT_CLASSES[variant];
    const foregroundHex = FOREGROUND_HEX[variant][colorScheme];

    const label = useMemo(() => {
      if (variant === 'tagged-all') {
        return 'All';
      }
      const trimmed = displayName.trim();
      const safe = trimmed.length > 0 ? trimmed : 'user';
      return `@${safe}`;
    }, [variant, displayName]);

    const accessibilityLabel = useMemo(() => {
      switch (variant) {
        case 'tag-you':
          return 'Mention of you';
        case 'tagged-other':
          return 'Mention';
        case 'tagged-own':
          return 'Your mention';
        case 'tagged-all':
          return 'Mention of everyone';
      }
    }, [variant]);

    const textStyle: TextStyle = {
      fontWeight: tokens.typography.fontWeight.bold.toString() as TextStyle['fontWeight'],
    };

    // The avatar slot only applies to the two user-targeted chips; `All`
    // owns a group glyph and `tag-you` is a bare highlighted name.
    const showAvatar =
      (variant === 'tagged-other' || variant === 'tagged-own') &&
      (avatarUri != null || (avatarInitials != null && avatarInitials.trim().length > 0));

    const leading = useMemo(() => {
      if (variant === 'tagged-all') {
        return <Users size={14} color={foregroundHex} />;
      }
      if (!showAvatar) {
        return null;
      }
      if (avatarUri != null) {
        return <Image source={{ uri: avatarUri }} style={styles.avatar} />;
      }
      return (
        <View
          className="bg-[#537EFF26]"
          style={[styles.avatar, styles.avatarFallback]}
        >
          <Text style={[styles.avatarInitials, { color: foregroundHex }]}>
            {(avatarInitials ?? '').trim().slice(0, 2).toUpperCase()}
          </Text>
        </View>
      );
    }, [variant, showAvatar, avatarUri, avatarInitials, foregroundHex]);

    const content = (
      <View
        className={variantClasses.background}
        style={styles.pill}
        testID={testID}
      >
        {leading}
        <Text className={variantClasses.text} style={[styles.text, textStyle]}>
          {label}
        </Text>
      </View>
    );

    if (!onPress) {
      return content;
    }

    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint="Opens the mentioned user"
      >
        {content}
      </Pressable>
    );
  },
);

MentionPill.displayName = 'MentionPill';

const MENTION_FONT_SIZE = 16;
const AVATAR_SIZE = 16;

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xxs, // 2
    paddingHorizontal: tokens.spacing.xs, // 4
    paddingVertical: 3,
    borderRadius: tokens.radius.xs, // 6
    alignSelf: 'flex-start',
  },
  text: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: MENTION_FONT_SIZE,
    lineHeight: MENTION_FONT_SIZE * tokens.typography.lineHeight.normal,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 4,
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 9,
    fontWeight: '700',
  },
});
